import { createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { ArrowUpIcon, BookmarkIcon, CheckIcon } from "@heroicons/react/24/outline";
import useTranslation from "../../hooks/useTranslation";
import { useUserSettings } from "../../hooks/useUserSettings";
import { useLanguageSpace } from "../../hooks/useLanguageSpace";
import { resolveQuickTranslateTargetLanguage } from "../../utils/quickTranslateTargetLanguage";
import { resolveQuickTranslateVocabularyPayload } from "../../utils/quickTranslateVocabularyPayload";
import { getCsrfCookie } from "../../lib/utils";
import {
  VOCABULARY_ADDED_EVENT,
  VOCABULARY_ADDED_FLAG,
} from "../../hooks/useVocabularyCreate";

type MinimalTranslateResponse = {
  translation: string;
  saved_vocab_id?: number | null;
  example_sentence?: string | null;
  source_example_sentence?: string | null;
  resolved_source_lang?: string;
  resolved_target_lang?: string;
};

type TranslationSegment = {
  value: string;
  isToken: boolean;
  tokenIndex: number | null;
};

type QuickTranslateLayoutVariant = "editorial" | "legacy";

const TRANSLATED_TOKEN_PATTERN = /[\p{L}\p{M}\p{N}]+(?:['’][\p{L}\p{M}\p{N}]+)*/gu;
const WORD_SEGMENT_PATTERN = /[\p{L}\p{M}\p{N}]+(?:['’][\p{L}\p{M}\p{N}]+)*/gu;
const QUICK_TRANSLATE_LAYOUT_VARIANT: QuickTranslateLayoutVariant = "legacy";

function QuickTranslatePage() {
  const { translate } = useTranslation();
  const queryClient = useQueryClient();
  const userSettings = useUserSettings();
  const motherTongue = userSettings.data?.mother_tongue ?? "GERMAN";
  const { languageSpace } = useLanguageSpace("ALL");
  const targetLanguage = resolveQuickTranslateTargetLanguage(
    languageSpace,
    motherTongue,
  );
  const [text, setText] = useState("");
  const [translation, setTranslation] = useState("");
  const [isTranslating, setIsTranslating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedVocabId, setSavedVocabId] = useState<number | null>(null);
  const [lastSavedWord, setLastSavedWord] = useState<string | null>(null);
  const [exampleSentence, setExampleSentence] = useState("");
  const [sourceExampleSentence, setSourceExampleSentence] = useState("");
  const [resolvedSourceLang, setResolvedSourceLang] = useState<string>("AUTO");
  const [resolvedTargetLang, setResolvedTargetLang] = useState<string>(targetLanguage);
  const [selectedTranslatedTokenIndexes, setSelectedTranslatedTokenIndexes] = useState<
    number[]
  >([]);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const latestSubmitIdRef = useRef(0);
  const wordCount = useMemo(
    () => (text.trim() ? text.trim().split(/\s+/).length : 0),
    [text],
  );
  const translationSegments = useMemo(() => {
    if (!translation) return [] as TranslationSegment[];
    const segments: TranslationSegment[] = [];
    let lastIndex = 0;
    let tokenIndex = 0;
    for (const match of translation.matchAll(TRANSLATED_TOKEN_PATTERN)) {
      const start = match.index ?? 0;
      const token = match[0] ?? "";
      if (start > lastIndex) {
        segments.push({
          value: translation.slice(lastIndex, start),
          isToken: false,
          tokenIndex: null,
        });
      }
      segments.push({ value: token, isToken: true, tokenIndex });
      tokenIndex += 1;
      lastIndex = start + token.length;
    }
    if (lastIndex < translation.length) {
      segments.push({
        value: translation.slice(lastIndex),
        isToken: false,
        tokenIndex: null,
      });
    }
    return segments;
  }, [translation]);
  const selectedPhrase = useMemo(
    () =>
      translationSegments
        .filter(
          (segment) =>
            segment.isToken &&
            segment.tokenIndex !== null &&
            selectedTranslatedTokenIndexes.includes(segment.tokenIndex),
        )
        .map((segment) => segment.value)
        .join(" ")
        .trim(),
    [translationSegments, selectedTranslatedTokenIndexes],
  );
  const translationWordCounts = useMemo(() => {
    const tokens = translation.match(WORD_SEGMENT_PATTERN) ?? [];
    const counts = new Map<string, number>();
    for (const token of tokens.map((item) => item.toLowerCase())) {
      counts.set(token, (counts.get(token) ?? 0) + 1);
    }
    return counts;
  }, [translation]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
    el.style.overflowY = el.scrollHeight > 220 ? "auto" : "hidden";
  }, [text]);

  useEffect(() => {
    setSavedVocabId(null);
    setLastSavedWord(null);
    setSelectedTranslatedTokenIndexes([]);
    setTranslation("");
    setExampleSentence("");
    setSourceExampleSentence("");
    setError(null);
    if (!text.trim()) {
      latestSubmitIdRef.current += 1;
      setIsTranslating(false);
    }
  }, [text]);

  const submitTranslation = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const submitId = latestSubmitIdRef.current + 1;
    latestSubmitIdRef.current = submitId;
    setIsTranslating(true);
    setError(null);
    setSavedVocabId(null);
    setLastSavedWord(null);
    setSelectedTranslatedTokenIndexes([]);
    setTranslation("");
    setExampleSentence("");
    setSourceExampleSentence("");
    try {
      const res = await fetch("/api/reeprise/translate/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFTOKEN": getCsrfCookie(),
        },
        credentials: "include",
        body: JSON.stringify({
          text: trimmed,
          learning_lang: targetLanguage,
          save: false,
        }),
      });
      if (!res.ok) {
        if (latestSubmitIdRef.current === submitId) {
          setTranslation("");
          setExampleSentence("");
          setSourceExampleSentence("");
        }
        return;
      }
      const data = (await res.json()) as MinimalTranslateResponse;
      if (latestSubmitIdRef.current !== submitId) {
        return;
      }
      setTranslation(data.translation || "");
      setResolvedSourceLang(data.resolved_source_lang || "AUTO");
      setResolvedTargetLang(data.resolved_target_lang || targetLanguage);
      const targetExampleSentence = (data.example_sentence || data.translation || "").trim();
      setExampleSentence(targetExampleSentence);
      setSourceExampleSentence("");
      // Do not block first-result rendering on the mother-tongue fallback sentence.
      if (latestSubmitIdRef.current === submitId) {
        setIsTranslating(false);
      }
      if (targetExampleSentence) {
        void (async () => {
          try {
            const motherTongueResponse = await fetch("/api/reeprise/translate/", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-CSRFTOKEN": getCsrfCookie(),
              },
              credentials: "include",
              body: JSON.stringify({
                text: targetExampleSentence,
                learning_lang: motherTongue,
                source_lang: data.resolved_target_lang || targetLanguage,
                save: false,
              }),
            });
            if (!motherTongueResponse.ok) {
              return;
            }
            const motherTongueData =
              (await motherTongueResponse.json()) as MinimalTranslateResponse;
            if (latestSubmitIdRef.current === submitId) {
              setSourceExampleSentence(motherTongueData.translation || "");
            }
          } catch {
            // Keep first translation visible even if fallback translation fails.
          }
        })();
      }
    } finally {
      if (latestSubmitIdRef.current === submitId) {
        setIsTranslating(false);
      }
    }
  };

  const handleTranslateSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submitTranslation();
  };

  const saveCurrent = async () => {
    const trimmed = text.trim();
    if (!trimmed || !translation || wordCount > 4) return;
    setIsSaving(true);
    setError(null);
    try {
      const payload = resolveQuickTranslateVocabularyPayload({
        inputText: trimmed,
        translatedText: translation,
        learningLanguage: targetLanguage,
        motherTongue,
        resolvedSourceLang,
        resolvedTargetLang,
        exampleSentence,
      });
      const res = await fetch("/api/reeprise/vocabulary/agent_add/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFTOKEN": getCsrfCookie(),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        setError(translate("general_error"));
        return;
      }
      const data = (await res.json()) as { id?: number };
      setSavedVocabId(data.id ?? null);
      setLastSavedWord(payload.word);
      if (typeof document !== "undefined") {
        document.dispatchEvent(new CustomEvent(VOCABULARY_ADDED_EVENT));
      }
      if (typeof window !== "undefined") {
        window.localStorage.setItem(VOCABULARY_ADDED_FLAG, "1");
      }
      void queryClient.invalidateQueries({
        queryKey: ["struggle-words"],
      });
    } finally {
      setIsSaving(false);
    }
  };

  const saveSelectedPhrase = async () => {
    if (!selectedPhrase || selectedTranslatedTokenIndexes.length > 4) return;
    setIsSaving(true);
    setError(null);
    try {
      const res = await fetch("/api/reeprise/vocabulary/agent_add/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFTOKEN": getCsrfCookie(),
        },
        credentials: "include",
        body: JSON.stringify({
          word: selectedPhrase,
          word_language: targetLanguage,
          translation_language: motherTongue,
          initial_translation: text.trim(),
          sentence: exampleSentence || undefined,
        }),
      });
      if (!res.ok) {
        setError(translate("general_error"));
        return;
      }
      const data = (await res.json()) as { id?: number };
      setSavedVocabId(data.id ?? null);
      setLastSavedWord(selectedPhrase);
      if (typeof document !== "undefined") {
        document.dispatchEvent(new CustomEvent(VOCABULARY_ADDED_EVENT));
      }
      if (typeof window !== "undefined") {
        window.localStorage.setItem(VOCABULARY_ADDED_FLAG, "1");
      }
      void queryClient.invalidateQueries({
        queryKey: ["struggle-words"],
      });
    } finally {
      setIsSaving(false);
    }
  };

  const renderSentenceWithHighlights = (
    sentence: string,
    highlightWordCounts: Map<string, number>,
  ) => {
    if (!sentence) return null;
    const pieces: Array<{ text: string; isWord: boolean }> = [];
    let lastIndex = 0;
    for (const match of sentence.matchAll(WORD_SEGMENT_PATTERN)) {
      const start = match.index ?? 0;
      const token = match[0] ?? "";
      if (start > lastIndex) {
        pieces.push({ text: sentence.slice(lastIndex, start), isWord: false });
      }
      pieces.push({ text: token, isWord: true });
      lastIndex = start + token.length;
    }
    if (lastIndex < sentence.length) {
      pieces.push({ text: sentence.slice(lastIndex), isWord: false });
    }
    const remaining = new Map(highlightWordCounts);
    return (
      <>
        {pieces.map((piece, index) => {
          if (!piece.isWord) {
            return <span key={`sep-${index}`}>{piece.text}</span>;
          }
          const lowered = piece.text.toLowerCase();
          const available = remaining.get(lowered) ?? 0;
          const isHighlight = available > 0;
          if (isHighlight) {
            remaining.set(lowered, available - 1);
          }
          return (
            <span
              key={`word-${index}`}
              className={isHighlight ? "text-emerald-700" : undefined}
            >
              {piece.text}
            </span>
          );
        })}
      </>
    );
  };

  const mainContent = (
    <>
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 text-left">
        <div className="flex flex-wrap items-center gap-2 border-b border-stone-300 pb-3">
          {["[T] TRANSLATE", "[E] EXAMPLES", "[V] VOCAB", "[S] SAVE"].map((chip) => (
            <span
              key={chip}
              className="inline-flex items-center rounded-sm bg-stone-200 px-2.5 py-1 font-ABee-zee text-xs font-semibold tracking-wide text-stone-700"
            >
              {chip}
            </span>
          ))}
        </div>

        <div className="grid gap-6 md:grid-cols-[220px_minmax(0,1fr)]">
          <aside className="space-y-3 border-b border-stone-300 pb-4 md:border-b-0 md:border-r md:pb-0 md:pr-4">
            <p className="font-ABee-zee text-xs font-semibold tracking-wide text-stone-500">
              / MODE
            </p>
            <p className="font-ABee-zee text-sm text-stone-700">
              {translate("quick_translate_label")}
            </p>
            <p className="font-ABee-zee text-xs leading-5 text-stone-500">
              Enter to translate. Shift+Enter for line break.
            </p>
          </aside>

          <section className="space-y-4">
            <form onSubmit={handleTranslateSubmit} className="space-y-4">
              <div className="w-full border-b border-stone-300 pb-3">
                <textarea
                  ref={inputRef}
                  autoFocus
                  value={text}
                  rows={1}
                  spellCheck={false}
                  autoCorrect="off"
                  autoCapitalize="off"
                  onChange={(event) => setText(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      void submitTranslation();
                    }
                  }}
                  placeholder={translate("quick_translate_minimal_placeholder")}
                  className="w-full resize-none border-0 bg-transparent p-0 font-ABee-zee text-base leading-[1.6] text-stone-800 placeholder:text-stone-500 focus:outline-none focus:ring-0 sm:text-lg"
                />
              </div>
            </form>

            {isTranslating ? (
              <p className="font-ABee-zee text-base leading-[1.6] text-stone-500 sm:text-lg">
                {translate("quick_translate_translating")}
              </p>
            ) : null}

            {!isTranslating && translation ? (
              <div className="space-y-4">
                <div className="border-b border-stone-300 pb-3">
                  <p className="font-ABee-zee text-base leading-[1.6] text-stone-800 sm:text-lg">
                    {translationSegments.map((segment, index) => {
                      if (!segment.isToken || segment.tokenIndex === null) {
                        return <span key={`seg-${index}`}>{segment.value}</span>;
                      }
                      const isSelected = selectedTranslatedTokenIndexes.includes(
                        segment.tokenIndex,
                      );
                      return (
                        <button
                          key={`tok-${segment.tokenIndex}`}
                          type="button"
                          onClick={() =>
                            setSelectedTranslatedTokenIndexes((prev) => {
                              if (prev.includes(segment.tokenIndex!)) {
                                return prev.filter((item) => item !== segment.tokenIndex);
                              }
                              if (wordCount > 4 && prev.length >= 4) {
                                return prev;
                              }
                              return [...prev, segment.tokenIndex!];
                            })
                          }
                          className={`inline rounded p-0 text-left ${
                            isSelected
                              ? "bg-stone-900 text-white"
                              : "text-stone-800 hover:bg-stone-200"
                          }`}
                        >
                          {segment.value}
                        </button>
                      );
                    })}
                  </p>
                </div>

                {exampleSentence ? (
                  <p className="font-ABee-zee text-sm leading-7 text-stone-600">
                    {renderSentenceWithHighlights(exampleSentence, translationWordCounts)}
                  </p>
                ) : null}

                {sourceExampleSentence ? (
                  <p className="font-ABee-zee text-sm leading-7 text-stone-600">
                    {sourceExampleSentence}
                  </p>
                ) : null}

                {wordCount <= 4 ? (
                  <button
                    type="button"
                    onClick={() => void saveCurrent()}
                    disabled={isSaving || savedVocabId !== null}
                    className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-800 disabled:opacity-50"
                  >
                    {savedVocabId ? <CheckIcon className="h-4 w-4" /> : <BookmarkIcon className="h-4 w-4" />}
                    {savedVocabId
                      ? translate("quick_translate_saved")
                      : isSaving
                        ? translate("quick_translate_saving")
                        : translate("quick_translate_save_action")}
                  </button>
                ) : (
                  <div className="space-y-2">
                    <p className="font-ABee-zee text-xs text-stone-500">
                      {translate("quick_translate_pick_word_hint")}
                    </p>
                    <button
                      type="button"
                      onClick={() => void saveSelectedPhrase()}
                      disabled={
                        isSaving ||
                        !selectedPhrase ||
                        selectedTranslatedTokenIndexes.length > 4 ||
                        savedVocabId !== null
                      }
                      className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-800 disabled:opacity-50"
                    >
                      {savedVocabId ? <CheckIcon className="h-4 w-4" /> : <BookmarkIcon className="h-4 w-4" />}
                      {savedVocabId
                        ? translate("quick_translate_saved")
                        : isSaving
                          ? translate("quick_translate_saving")
                          : selectedTranslatedTokenIndexes.length > 0 &&
                              selectedTranslatedTokenIndexes.length <= 4
                            ? translate("quick_translate_save_selected_word_action", {
                                word: selectedPhrase,
                              })
                            : translate("quick_translate_save_action_limited")}
                    </button>
                  </div>
                )}

                {lastSavedWord ? (
                  <p className="font-ABee-zee text-xs text-emerald-700">
                    {translate("quick_translate_saved_prefix")} “{lastSavedWord}”
                  </p>
                ) : null}
              </div>
            ) : null}
          </section>
        </div>
      </div>
      {QUICK_TRANSLATE_LAYOUT_VARIANT === "editorial" ? (
        <button
          type="button"
          onClick={() => void submitTranslation()}
          disabled={isTranslating || !text.trim()}
          aria-label={translate("quick_translate_action_neutral")}
          className="fixed right-6 bottom-6 z-20 inline-flex h-10 w-10 items-center justify-center rounded-full bg-stone-950 text-white transition hover:bg-stone-800 disabled:bg-stone-200 disabled:text-stone-400"
        >
          <ArrowUpIcon className="h-4 w-4" />
        </button>
      ) : null}
    </>
  );

  const legacyMainContent = (
    <div className="mx-auto flex w-full max-w-3xl flex-col items-start gap-4 text-left">
      <form
        onSubmit={handleTranslateSubmit}
        className="flex w-full items-end gap-3 rounded-2xl border border-stone-200 bg-white px-4 py-3"
      >
        <textarea
          ref={inputRef}
          autoFocus
          value={text}
          rows={1}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              void submitTranslation();
            }
          }}
          placeholder={translate("quick_translate_minimal_placeholder")}
          className="w-full resize-none border-0 bg-transparent p-0 font-ABee-zee text-base leading-[1.6] text-stone-800 placeholder:text-stone-500 focus:outline-none focus:ring-0 sm:text-lg"
        />
        <button
          type="submit"
          disabled={isTranslating || !text.trim()}
          aria-label={translate("quick_translate_action_neutral")}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-stone-950 text-white transition hover:bg-stone-800 disabled:bg-stone-200 disabled:text-stone-400"
        >
          <ArrowUpIcon className="h-4 w-4" />
        </button>
      </form>

      {isTranslating ? (
        <p className="ml-4 font-ABee-zee text-base leading-[1.6] text-stone-500 sm:text-lg">
          {translate("quick_translate_translating")}
        </p>
      ) : null}
      {!isTranslating && translation ? (
        <div className="ml-4 space-y-2">
          <p className="font-ABee-zee text-base leading-[1.6] text-stone-800 sm:text-lg">
            {translationSegments.map((segment, index) => {
              if (!segment.isToken || segment.tokenIndex === null) {
                return <span key={`legacy-seg-${index}`}>{segment.value}</span>;
              }
              const isSelected = selectedTranslatedTokenIndexes.includes(
                segment.tokenIndex,
              );
              return (
                <button
                  key={`legacy-tok-${segment.tokenIndex}`}
                  type="button"
                  onClick={() =>
                    setSelectedTranslatedTokenIndexes((prev) => {
                      if (prev.includes(segment.tokenIndex!)) {
                        return prev.filter((item) => item !== segment.tokenIndex);
                      }
                      if (wordCount > 4 && prev.length >= 4) {
                        return prev;
                      }
                      return [...prev, segment.tokenIndex!];
                    })
                  }
                  className={`inline rounded p-0 text-left ${
                    isSelected
                      ? "bg-stone-900 text-white"
                      : "text-stone-800 hover:bg-stone-200"
                  }`}
                >
                  {segment.value}
                </button>
              );
            })}
          </p>
          {exampleSentence ? (
            <p className="text-sm text-stone-600">
              {renderSentenceWithHighlights(exampleSentence, translationWordCounts)}
            </p>
          ) : null}
          {sourceExampleSentence ? (
            <p className="text-sm text-stone-600">{sourceExampleSentence}</p>
          ) : null}
          {wordCount <= 4 ? (
            <button
              type="button"
              onClick={() => void saveCurrent()}
              disabled={isSaving || savedVocabId !== null}
              className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-800 disabled:opacity-50"
            >
              {savedVocabId ? <CheckIcon className="h-4 w-4" /> : <BookmarkIcon className="h-4 w-4" />}
              {savedVocabId
                ? translate("quick_translate_saved")
                : isSaving
                  ? translate("quick_translate_saving")
                  : translate("quick_translate_save_action")}
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-stone-500">
                {translate("quick_translate_pick_word_hint")}
              </p>
              <button
                type="button"
                onClick={() => void saveSelectedPhrase()}
                disabled={
                  isSaving ||
                  !selectedPhrase ||
                  selectedTranslatedTokenIndexes.length > 4 ||
                  savedVocabId !== null
                }
                className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-800 disabled:opacity-50"
              >
                {savedVocabId ? <CheckIcon className="h-4 w-4" /> : <BookmarkIcon className="h-4 w-4" />}
                {savedVocabId
                  ? translate("quick_translate_saved")
                  : isSaving
                    ? translate("quick_translate_saving")
                    : selectedTranslatedTokenIndexes.length > 0 &&
                        selectedTranslatedTokenIndexes.length <= 4
                      ? translate("quick_translate_save_selected_word_action", {
                          word: selectedPhrase,
                        })
                      : translate("quick_translate_save_action_limited")}
              </button>
            </div>
          )}
          {lastSavedWord ? (
            <p className="text-xs text-emerald-700">
              {translate("quick_translate_saved_prefix")} “{lastSavedWord}”
            </p>
          ) : null}
        </div>
      ) : null}
      {error ? <p className="ml-4 text-xs text-rose-600">{error}</p> : null}
    </div>
  );

  return (
    <div
      className={
        QUICK_TRANSLATE_LAYOUT_VARIANT === "editorial"
          ? "flex min-h-[calc(100dvh-4rem)] w-full items-start bg-[#fbfbf8] px-6 pt-8 sm:pt-10"
          : "flex min-h-[calc(100dvh-4rem)] w-full items-start bg-[#fbfbf8] px-4 pt-6 pb-10 sm:px-8 sm:pt-8"
      }
    >
      {QUICK_TRANSLATE_LAYOUT_VARIANT === "editorial" ? (
        mainContent
      ) : (
        legacyMainContent
      )}
    </div>
  );
}

export const Route = createFileRoute("/app/quick_translate")({
  component: QuickTranslatePage,
});
